import delve from 'dlv'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import useScript from 'hooks/useScript'
import fetch from 'isomorphic-unfetch'
import { useRef, useState, useEffect, useCallback } from 'react'
import { amplitudeTrack } from 'src/lib/Amplitude/analytics'

const useEmbedHubSpotForm = ({ portalId, formId }) => {
  const [loaded, error] = useScript({
    src: `https://js.hsforms.net/forms/embed/developer/${portalId}.js`,
  })
  const [formCreated, setFormCreated] = useState(false)
  const formRef = useRef(null)

  const handleMessageEvent = useCallback(
    async (event) => {
      if (event.data.formGuid === formId && event.data.accepted) {
        amplitudeTrack('didSubmittedHubspotForm', { id: formId })
      }
    },
    [formId]
  )

  useEffect(() => {
    window.addEventListener('message', handleMessageEvent)
    return () => {
      window.removeEventListener('message', handleMessageEvent)
    }
  }, [handleMessageEvent])

  const loadForm = useCallback(
    async (form) => {
      if (!form || formCreated) return

      const result = await fetch('/api/country').then((res) => res.json())

      amplitudeTrack('didLoadedHubspotForm', { id: formId })

      if (result?.showDefaultConsentText === false) {
        // Remove existing .hs-richtext elements
        form
          .querySelectorAll('.hs-richtext')
          .forEach((element) => element.remove())

        // Add the new consent message
        const consentDiv = document.createElement('div')
        consentDiv.classList.add('hs-richtext')

        const consentText = document.createElement('p')
        consentText.innerText =
          'Strapi needs the contact information you provide to us to contact you about our products and services. You may unsubscribe from these communications at any time. For information on how to unsubscribe, as well as our privacy practices and commitment to protecting your privacy, check out our Privacy Policy.'

        consentDiv.appendChild(consentText)

        const legalContainer = form.querySelector('.legal-consent-container')
        if (legalContainer) {
          legalContainer.appendChild(consentDiv)
        }
      }

      form.querySelectorAll('.hs-richtext').forEach((pElement) => {
        pElement.style.fontSize = '10px'
        pElement.style.lineHeight = '1.8'
      })

      form.addEventListener('submit', () => {
        amplitudeTrack('didSubmitHubspotForm', { id: formId })
      })

      form.addEventListener('submitted', () => {
        amplitudeTrack('didSubmittedHubspotForm', { id: formId })
      })

      formRef.current = form // Ensure formRef is set for later use

      setFormCreated(true)
    },
    [formId]
  )

  useIsomorphicLayoutEffect(() => {
    if (!formId) return

    // Watch for form to appear
    const observer = new MutationObserver(() => {
      const form = document.querySelector(
        `form[data-hsfc-id="Form"][data-form-id="${formId}"]`
      )
      if (form && !formCreated) {
        loadForm(form)
        observer.disconnect()
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [loaded, formCreated])

  return { loaded, formCreated, error }
}

export default useEmbedHubSpotForm
